import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  ErrorOutlined,
  HourglassEmpty,
  ReportProblem,
  TaskAlt,
} from "@mui/icons-material";
import type {
  DocumentValidation,
  DocumentValidationStatus,
  DocumentVersion,
} from "@/api/types";
import { useOverrideValidation } from "@/api/hooks/documents";
import { DelegationLogReviewPanel } from "./DelegationLogReviewPanel";
import { GcpCertReviewPanel } from "./GcpCertReviewPanel";
import { RotationReviewPanel } from "./RotationReviewPanel";

interface Props {
  documentId: string;
  validations: DocumentValidation[];
  /** Optional: called with the updated row whenever an override
   * succeeds. Lets the upload flow detect when there are no remaining
   * blocking failures and close itself. */
  onOverridden?: (row: DocumentValidation) => void;
  /** Optional: called with the updated row when a NEEDS_REVIEW
   * validation is confirmed. Same semantics as onOverridden — the
   * parent (drawer or upload form) reconciles its local state. */
  onConfirmed?: (row: DocumentValidation) => void;
  /** Versions in scope. When supplied, the section can launch the
   * GcpCertReviewPanel for a NEEDS_REVIEW row by looking up the
   * version's content_type and version_number. Without this map,
   * NEEDS_REVIEW rows render the pill but no Review button. */
  versionsById?: Record<string, DocumentVersion>;
}

const VALIDATOR_LABELS: Record<string, string> = {
  "cv.name_match": "CV name matches investigator",
  "gcp.cert_review": "GCP training certificate",
};

const STATUS_META: Record<
  DocumentValidationStatus,
  {
    label: string;
    color: "success" | "error" | "warning" | "default" | "info";
    Icon: typeof CheckCircle;
  }
> = {
  passed: { label: "Passed", color: "success", Icon: CheckCircle },
  failed: { label: "Mismatch", color: "error", Icon: ReportProblem },
  pending: { label: "Pending", color: "info", Icon: HourglassEmpty },
  needs_review: { label: "Needs review", color: "info", Icon: HourglassEmpty },
  error: { label: "Error", color: "warning", Icon: ErrorOutlined },
  overridden: { label: "Reviewed (override)", color: "default", Icon: TaskAlt },
};

function describeGcp(v: DocumentValidation): string | null {
  if (v.validator_id !== "gcp.cert_review") return null;
  const extracted = v.extracted_value as
    | {
        candidate_name?: string;
        completion_date?: string;
        expiration_date?: string | null;
      }
    | null;
  if (!extracted) return null;
  const parts: string[] = [];
  if (extracted.candidate_name) parts.push(extracted.candidate_name);
  if (extracted.completion_date)
    parts.push(`completed ${extracted.completion_date}`);
  if (extracted.expiration_date)
    parts.push(`expires ${extracted.expiration_date}`);
  return parts.join(" · ");
}

function describe(v: DocumentValidation): string {
  if (v.status === "needs_review") {
    const gcp = describeGcp(v);
    if (gcp) return gcp;
    return "Waiting for review";
  }
  const extracted = v.extracted_value as
    | { raw?: string; score?: number; threshold?: number; source?: string }
    | null;
  const expected = v.expected_value as
    | { first_name?: string; last_name?: string }
    | null;
  const expectedName = expected
    ? `${expected.first_name ?? ""} ${expected.last_name ?? ""}`.trim()
    : "";
  switch (v.status) {
    case "passed":
      return (
        describeGcp(v) ||
        [
          extracted?.raw ? `Extracted: "${extracted.raw}"` : null,
          expectedName ? `Expected: ${expectedName}` : null,
          extracted?.score !== undefined
            ? `similarity ${extracted.score}/100`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      );
    case "failed":
      return v.error_message ?? "Validation failed";
    case "error":
      return v.error_message ?? "Validator could not run";
    case "overridden":
      return v.override_reason
        ? `Reviewed: ${v.override_reason}`
        : "Reviewed and accepted";
    case "pending":
      return "Waiting for validator to run";
  }
}

export function DocumentValidationsSection({
  documentId,
  validations,
  onOverridden,
  onConfirmed,
  versionsById,
}: Props) {
  const [overrideTarget, setOverrideTarget] =
    useState<DocumentValidation | null>(null);
  const [reviewTarget, setReviewTarget] =
    useState<DocumentValidation | null>(null);

  if (validations.length === 0) return null;

  const reviewVersion =
    reviewTarget && versionsById
      ? versionsById[reviewTarget.version_id]
      : undefined;

  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Validations
      </Typography>
      <Stack spacing={1}>
        {validations.map((v) => {
          const meta = STATUS_META[v.status];
          const Icon = meta.Icon;
          const label = VALIDATOR_LABELS[v.validator_id] ?? v.validator_id;
          return (
            <Box
              key={v.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.25,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Tooltip title={meta.label}>
                <Chip
                  size="small"
                  color={meta.color}
                  icon={<Icon style={{ fontSize: 16 }} />}
                  label={meta.label}
                  variant={v.status === "overridden" ? "outlined" : "filled"}
                />
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", wordBreak: "break-word" }}
                >
                  {describe(v)}
                </Typography>
              </Box>
              {v.status === "failed" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setOverrideTarget(v)}
                >
                  Mark reviewed
                </Button>
              )}
              {v.status === "needs_review" &&
                versionsById?.[v.version_id] &&
                // Orientation review only opens cleanly inside the
                // upload-flow drawer (where pdfjs has been initialized
                // on the same canvas instance). Re-opening it from a
                // detail-view dialog renders blank canvases. Hide the
                // button for orientation rows so we have one path —
                // the inline rotation panel during upload. (Other
                // NEEDS_REVIEW validators stay openable here.)
                v.validator_id !== "pdf.orientation_check" && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setReviewTarget(v)}
                  >
                    Review & confirm
                  </Button>
                )}
            </Box>
          );
        })}
      </Stack>

      {/* The right panel depends on which validator flagged the row.
       * Orientation has its own pypdf-driven flow; delegation logs
       * extract a roster; everything else is the extract-and-confirm
       * cert path. */}
      <Dialog
        open={reviewTarget !== null}
        onClose={() => setReviewTarget(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {reviewTarget?.validator_id === "pdf.orientation_check"
            ? "Correct page orientation"
            : reviewTarget?.validator_id === "delegation_log.review"
              ? "Review delegation log"
              : "Review certificate"}
        </DialogTitle>
        <DialogContent>
          {reviewTarget && reviewVersion && (
            <>
              {reviewTarget.validator_id === "pdf.orientation_check" && (
                <RotationReviewPanel
                  documentId={documentId}
                  versionId={reviewTarget.version_id}
                  validation={reviewTarget}
                  onApplied={(_newVersion, _validations) => {
                    // After rotation, the validation row flips to
                    // PASSED on v1 and a fresh row lands on v2. The
                    // parent's onConfirmed is the simplest signal —
                    // pass through the original (now-PASSED) row so
                    // the drawer refetches and picks up v2 +
                    // re-run validations.
                    onConfirmed?.({
                      ...reviewTarget,
                      status: "passed",
                    });
                    setReviewTarget(null);
                  }}
                  onCancel={() => setReviewTarget(null)}
                />
              )}
              {reviewTarget.validator_id === "delegation_log.review" && (
                <DelegationLogReviewPanel
                  documentId={documentId}
                  versionId={reviewTarget.version_id}
                  contentType={reviewVersion.content_type}
                  versionLabel={`v${reviewVersion.version_number}`}
                  validation={reviewTarget}
                  onConfirmed={(updated) => {
                    onConfirmed?.(updated);
                    setReviewTarget(null);
                  }}
                  onCancel={() => setReviewTarget(null)}
                />
              )}
              {reviewTarget.validator_id !== "pdf.orientation_check" &&
                reviewTarget.validator_id !== "delegation_log.review" && (
                  <GcpCertReviewPanel
                    documentId={documentId}
                    versionId={reviewTarget.version_id}
                    contentType={reviewVersion.content_type}
                    versionLabel={`v${reviewVersion.version_number}`}
                    validation={reviewTarget}
                    onConfirmed={(updated) => {
                      onConfirmed?.(updated);
                      setReviewTarget(null);
                    }}
                    onCancel={() => setReviewTarget(null)}
                  />
                )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <OverrideDialog
        documentId={documentId}
        validation={overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onOverridden={onOverridden}
      />
    </Box>
  );
}

function OverrideDialog({
  documentId,
  validation,
  onClose,
  onOverridden,
}: {
  documentId: string;
  validation: DocumentValidation | null;
  onClose: () => void;
  onOverridden?: (row: DocumentValidation) => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const override = useOverrideValidation(documentId);

  const open = validation !== null;

  const handleSubmit = async () => {
    if (!validation) return;
    setError(null);
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }
    try {
      const updated = await override.mutateAsync({
        validationId: validation.id,
        reason: reason.trim(),
      });
      onOverridden?.(updated);
      setReason("");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Override failed");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!override.isPending) {
          setReason("");
          setError(null);
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Mark validation as reviewed</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          Acknowledges the validation didn't match but you've manually
          confirmed the document is correct (e.g. preferred name, maiden
          name). The reason is recorded in the audit log.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          required
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={override.isPending}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={override.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={override.isPending}
          startIcon={
            override.isPending ? <CircularProgress size={14} /> : undefined
          }
        >
          {override.isPending ? "Saving…" : "Confirm review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
